import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { PageLoader } from '@/components/common/PageLoader';
import { ProtectedRoute, PublicRoute } from '@/routes/ProtectedRoute';
import { EmployeeAdminRoute } from '@/routes/EmployeeAdminRoute';
import { PollAdminRoute, PollAdminUserManagementRoute } from '@/routes/PollAdminRoute';
import { ROUTES } from '@/constants/routes';

const LoginPage = lazy(() =>
  import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import('@/pages/auth/RegisterPage').then((m) => ({ default: m.RegisterPage })),
);
const CreatePinPage = lazy(() =>
  import('@/pages/auth/CreatePinPage').then((m) => ({ default: m.CreatePinPage })),
);
const ForgotPinPage = lazy(() =>
  import('@/pages/auth/ForgotPinPage').then((m) => ({ default: m.ForgotPinPage })),
);
const DashboardPage = lazy(() =>
  import('@/pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const ProfilePage = lazy(() =>
  import('@/pages/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const EmployeeAdminPage = lazy(() =>
  import('@/pages/employees/EmployeeAdminPage').then((m) => ({ default: m.EmployeeAdminPage })),
);
const TaskImportPage = lazy(() =>
  import('@/pages/tasks/TaskImportPage').then((m) => ({ default: m.TaskImportPage })),
);
const SupportGuidePage = lazy(() =>
  import('@/pages/support/SupportGuidePage').then((m) => ({ default: m.SupportGuidePage })),
);
const PollAdminPage = lazy(() =>
  import('@/pages/polls/PollAdminPage').then((m) => ({ default: m.PollAdminPage })),
);
const PollsListPage = lazy(() =>
  import('@/pages/polls/PollsListPage').then((m) => ({ default: m.PollsListPage })),
);
const PollAdminsPage = lazy(() =>
  import('@/pages/polls/PollAdminsPage').then((m) => ({ default: m.PollAdminsPage })),
);
const PollResultsPage = lazy(() =>
  import('@/pages/polls/PollResultsPage').then((m) => ({ default: m.PollResultsPage })),
);
const PublicPollPage = lazy(() =>
  import('@/pages/polls/PublicPollPage').then((m) => ({ default: m.PublicPollPage })),
);
const PrivacyPolicyPage = lazy(() =>
  import('@/pages/legal/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage })),
);

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path={ROUTES.HOME} element={<Navigate to={ROUTES.DASHBOARD} replace />} />

        <Route
          path={ROUTES.LOGIN}
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.REGISTER}
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.CREATE_PIN}
          element={
            <PublicRoute>
              <CreatePinPage />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.FORGOT_PIN}
          element={
            <PublicRoute>
              <ForgotPinPage />
            </PublicRoute>
          }
        />

        <Route
          path={ROUTES.DASHBOARD}
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TASK_IMPORT}
          element={
            <ProtectedRoute>
              <TaskImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TASK_DETAIL}
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.PROFILE}
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SUPPORT_GUIDE}
          element={
            <ProtectedRoute>
              <SupportGuidePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.PUBLIC_POLL}
          element={<PublicPollPage />}
        />
        <Route
          path={ROUTES.PRIVACY_POLICY}
          element={<PrivacyPolicyPage />}
        />
        <Route
          path={ROUTES.EMPLOYEES}
          element={
            <EmployeeAdminRoute>
              <EmployeeAdminPage />
            </EmployeeAdminRoute>
          }
        />
        <Route
          path={ROUTES.POLLS}
          element={
            <PollAdminRoute>
              <PollAdminPage />
            </PollAdminRoute>
          }
        />
        <Route
          path={ROUTES.POLLS_ALL}
          element={
            <PollAdminRoute>
              <PollsListPage />
            </PollAdminRoute>
          }
        />
        <Route
          path={ROUTES.POLL_ADMINS}
          element={
            <PollAdminUserManagementRoute>
              <PollAdminsPage />
            </PollAdminUserManagementRoute>
          }
        />
        <Route
          path={ROUTES.POLL_NEW}
          element={
            <PollAdminRoute>
              <PollAdminPage />
            </PollAdminRoute>
          }
        />
        <Route
          path={ROUTES.POLL_EDIT}
          element={
            <PollAdminRoute>
              <PollAdminPage />
            </PollAdminRoute>
          }
        />
        <Route
          path={ROUTES.POLL_RESULTS}
          element={
            <PollAdminRoute>
              <PollResultsPage />
            </PollAdminRoute>
          }
        />
        <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
      </Routes>
    </Suspense>
  );
}
